import { SignedImg } from "@/components/shared/SignedImg";
import { Eye, EyeOff, Check, Pencil, Lock } from "lucide-react";
import { toast } from "sonner";
import { plur } from "@/lib/format";

const REDATABLE = "upload";
export const canRedate = (p) => p.source === REDATABLE;
const firstName = (n) => (n || "—").split(" ")[0];
export function dateLabel(d) {
  if (!d) return "";
  const t = new Date().toISOString().slice(0, 10);
  if (d === t) return "dnes";
  const [, m, dd] = d.split("-");
  return `${+dd}. ${+m}.`;
}

// Dlaždice: oko (deník) vlevo nahoře, výběr vpravo nahoře, datum vlevo dole,
// autor vpravo dole. Táhnout jde jen ručně nahraná fotka; u zamčené → toast.
export function GalleryGrid({ photos, canEye, mode, selected, onToggleSelect, onOpen, onEye, onRedateTap, onDragState, gridClass }) {
  return (
    <div className={gridClass || "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2"} data-testid="gallery-grid">
      {photos.map((p) => {
        const inDiary = p.status === "approved";
        const sel = selected.has(p.id);
        const editable = canRedate(p);
        // Ztlumení a zámek jen v režimu Změnit datum; v režimu Vybrat jde označit vše.
        const dimmed = mode === "redate" && !editable;
        const selectable = mode === "select" || (mode === "redate" && editable);
        return (
          <div key={p.id} data-testid={`gallery-tile-${p.id}`}
            draggable={mode === "redate"}
            onDragStart={(e) => {
              if (!editable) { e.preventDefault(); toast.error("Tuhle fotku nelze přesunout na jiné datum — přišla z docházky."); return; }
              e.dataTransfer.setData("text/plain", p.id);
              // Tažení vybrané fotky nese celý výběr — u kurzoru se ukáže počet.
              const n = sel ? selected.size : 1;
              if (n > 1) {
                const ghost = document.createElement("div");
                ghost.className = "bg-brand text-white rounded-full px-2 py-0.5 text-xs font-bold fixed left-0";
                ghost.style.top = "-100px";
                ghost.textContent = plur(n, "fotka", "fotky", "fotek");
                document.body.appendChild(ghost);
                e.dataTransfer.setDragImage(ghost, 12, 12);
                setTimeout(() => ghost.remove(), 0);
              }
              onDragState?.(n);
            }}
            onDragEnd={() => onDragState?.(0)}
            onClick={() => { if (mode) { if (selectable) onToggleSelect(p.id); } else onOpen(p); }}
            aria-disabled={dimmed || undefined}
            className={`relative aspect-square rounded-[12px] overflow-hidden border ${dimmed ? "cursor-not-allowed" : "cursor-pointer"} ${sel ? "border-brand border-2" : "border-border"}`}>
            <SignedImg url={p.url} alt={p.caption} thumb={220} className={`w-full h-full object-cover ${dimmed ? "opacity-60 grayscale" : ""}`} />
            {dimmed && (
              <div className="absolute inset-0 grid place-items-center text-white/90 pointer-events-none" data-testid={`tile-lock-overlay-${p.id}`}>
                <Lock size={26} />
              </div>
            )}
            {canEye && (
              <button type="button" data-testid={`tile-eye-${p.id}`} onClick={(e) => { e.stopPropagation(); onEye(p); }}
                className={`absolute top-1.5 left-1.5 w-8 h-8 rounded-full grid place-items-center ${inDiary ? "bg-brand text-white" : "bg-black/40 text-white/80"}`}
                title={inDiary ? "V deníku — investor vidí" : "Skryto z deníku"}>
                {inDiary ? <Eye size={15} /> : <EyeOff size={15} />}
              </button>
            )}
            {selectable && (
              <div data-testid={`tile-select-${p.id}`}
                className={`absolute top-1.5 right-1.5 w-7 h-7 rounded-full grid place-items-center border-2 ${sel ? "bg-brand border-brand text-white" : "bg-white/70 border-white"}`}>
                {sel && <Check size={15} />}
              </div>
            )}
            {editable ? (
              <button type="button" data-testid={`tile-date-edit-${p.id}`} title="Datum lze změnit"
                onClick={(e) => { e.stopPropagation(); onRedateTap?.(p); }}
                className="absolute bottom-1.5 left-1.5 inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-brand text-white">
                {dateLabel(p.date)} <Pencil size={11} />
              </button>
            ) : (
              <button type="button" data-testid={`tile-date-locked-${p.id}`} title="Datum z docházky — nelze měnit"
                onClick={(e) => { e.stopPropagation(); toast.error("Tuhle fotku nelze přesunout na jiné datum — přišla z docházky, výkazu nebo úkolu."); }}
                className="absolute bottom-1.5 left-1.5 inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full text-white"
                style={{ background: "rgba(40,40,44,.88)" }}>
                {dateLabel(p.date)} <Lock size={11} />
              </button>
            )}
            <span className="absolute bottom-1.5 right-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "rgba(255,255,255,.9)", color: "#111" }}>{firstName(p.author_name)}</span>
          </div>
        );
      })}
    </div>
  );
}
